import React from 'react';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';

import LabelChip, { labelColorToCss } from 'src/components/labels/LabelChip';
import type { ILeadLabel } from 'src/services/api';

import styles from 'src/components/labels/LabelChips.module.css';

type LabelLike = Pick<ILeadLabel, 'id' | 'name' | 'color'>;

type Props = {
    labels: LabelLike[];
    maxVisible?: number;
    size?: 'compact' | 'body';
    multiline?: boolean;
};

/**
 * Renders a row of lead-label chips, capped at ``maxVisible`` with a ``+N``
 * overflow chip. Lead labels carry their own ``color``.
 */
export default function LeadLabels({ labels, maxVisible = 2, size = 'compact', multiline = false }: Props) {
    const visibleLabels = labels.slice(0, maxVisible);
    const hiddenLabels = labels.slice(maxVisible);
    const numRemainingLabels = labels.length - visibleLabels.length;
    const isBodySize = size === 'body';

    if (labels.length === 0) {
        return null;
    }

    return (
        <div className={`${styles.root} ${multiline ? styles.multiline : ''}`}>
            {visibleLabels.map((label) => (
                <LabelChip key={label.id} name={label.name} color={label.color} size={size} />
            ))}

            {numRemainingLabels > 0 && (
                <Tooltip title={hiddenLabels.map((label) => label.name).join(', ')}>
                    <div
                        className={`${styles.remaining} ${isBodySize ? styles.remainingBody : ''}`}
                        style={{ '--label-color': labelColorToCss(hiddenLabels[0].color) } as React.CSSProperties}
                    >
                        <Typography variant="caption" className={styles.text}>
                            +{numRemainingLabels}
                        </Typography>
                    </div>
                </Tooltip>
            )}
        </div>
    );
}
